(ns ycweb.app.website.tracking
  "Google Tag Manager dataLayer tracking utilities, shared across the
   website SPA (cart, products, wishlist).

   Every push funnels through `push-to-datalayer!` / `push-ecommerce-event!`,
   which also feed the inspector: a bounded buffer of every push the SPA
   attempted, exposed as `window.ycTracking`, logging to the console by
   default everywhere except production."
  #?(:cljs (:require [goog.object :as gobj])))

;; ---------------------------------------------------------------------------
;; Inspector

(def ^:private inspector-max 200)

(def ^:private inspector-storage-key "yc-tracking-inspector")

(defonce ^:private inspector-events-atom (atom []))

(defonce ^:private inspector-state-atom
  (atom {:enabled? false :environment nil :warned? false :banner? false}))

(defn- inspector-enabled?
  "The localStorage override wins (\"1\" on, \"0\" off). Otherwise on
   wherever PHP's dataLayer says the page is not production, and off when
   the environment is unknown (no dataLayer at all)."
  [override environment]
  (case override
    "1" true
    "0" false
    (and (some? environment) (not= "production" environment))))

(defn- read-override []
  #?(:cljs
     (try
       (.getItem js/localStorage inspector-storage-key)
       (catch :default _ nil))
     :clj nil))

(defn read-environment
  "PHP seeds `dataLayer[0].environment` (local / staging / production)
   before the bundle runs."
  []
  #?(:cljs
     (try
       (when (exists? js/dataLayer)
         (some-> (aget js/dataLayer 0) (gobj/get "environment")))
       (catch :default _ nil))
     :clj nil))

(defn- tap!
  "Record an attempted push before the dataLayer check, so the inspector
   also sees pushes a missing GTM would swallow. Never allowed to break the
   real push."
  [event-data pushed? cleared?]
  #?(:cljs
     (try
       (let [{:keys [enabled? warned?]} @inspector-state-atom
             entry
             {:t (.now js/Date)
              :pushed? pushed?
              :cleared? cleared?
              :data event-data}]
         (swap! inspector-events-atom
           (fn [buffer]
             (conj
               (if (>= (count buffer) inspector-max)
                 (into [] (rest buffer))
                 buffer)
               entry)))
         (when enabled?
           (js/console.debug "[yc-tracking]"
             (or (:event event-data) "(no event)")
             (clj->js event-data))
           (when (and (not pushed?) (not warned?))
             (swap! inspector-state-atom assoc :warned? true)
             (js/console.warn
               "[yc-tracking] window.dataLayer is missing: GTM never loaded on this page, pushes are being dropped"))))
       (catch :default _ nil))))

(defn set-inspector!
  "Switch the inspector on or off for this browser (localStorage), effective
   immediately. Returns a one-line confirmation for the console."
  [enabled?]
  #?(:cljs
     (do
       (try
         (.setItem js/localStorage inspector-storage-key (if enabled? "1" "0"))
         (catch :default _ nil))
       (swap! inspector-state-atom assoc :enabled? enabled?)
       (str "[yc-tracking] inspector " (if enabled? "ON" "OFF")
         ", persisted in localStorage " inspector-storage-key))))

(defn clear-inspector-override!
  "Forget the localStorage override and go back to the environment default."
  []
  #?(:cljs
     (do
       (try
         (.removeItem js/localStorage inspector-storage-key)
         (catch :default _ nil))
       (swap! inspector-state-atom
         (fn [{:keys [environment] :as state}]
           (assoc state :enabled? (inspector-enabled? nil environment)))))))

(defn- inspector-entry->js
  "One console-table-friendly row per attempted push."
  [{:keys [t pushed? cleared? data]}]
  #?(:cljs
     (clj->js
       {:t t
        :event (or (:event data) "")
        :pushed pushed?
        :cleared cleared?
        :data data})))

(defn inspector-events->js
  "The buffered pushes as a JS array of console-table-friendly rows."
  []
  #?(:cljs
     (to-array (map inspector-entry->js @inspector-events-atom))))

(defn inspector-last->js []
  #?(:cljs
     (some-> (peek @inspector-events-atom) inspector-entry->js)))

(defn clear-inspector-events! []
  (reset! inspector-events-atom [])
  nil)

(defn inspector-status-map
  "Inspector state as data: enabled?, environment, the localStorage override,
   buffered event count and the GTM containers loaded on the page."
  []
  #?(:cljs
     (let [{:keys [enabled? environment]} @inspector-state-atom]
       {:enabled? enabled?
        :environment environment
        :override (read-override)
        :count (count @inspector-events-atom)
        :containers
        (->>
          (js-keys (or (gobj/get js/window "google_tag_manager") #js {}))
          (filter #(re-find #"^(?:GTM|G|AW|DC|UA)-" %))
          (vec))})
     :clj nil))

(defn- inspector-status []
  #?(:cljs
     (let [{:keys [enabled? environment override count containers]} (inspector-status-map)]
       (js-obj
         "enabled" enabled?
         "environment" environment
         "override" override
         "count" count
         "containers" (to-array containers)))))

(defn install-inspector!
  "Expose `window.ycTracking` and decide whether the inspector logs. Called
   from the website and minicart entry points (idempotent; the banner
   prints once per document). The PHP page writes the dataLayer var before
   any bundle runs, so the environment is readable here."
  []
  #?(:cljs
     (let [environment (read-environment)
           enabled? (inspector-enabled? (read-override) environment)]
       (swap! inspector-state-atom assoc :enabled? enabled? :environment environment)
       (aset js/window "ycTracking"
         (js-obj
           "events" (fn [] (inspector-events->js))
           "last" (fn [] (inspector-last->js))
           "clear" (fn [] (clear-inspector-events!))
           "enable" (fn [] (set-inspector! true))
           "disable" (fn [] (set-inspector! false))
           "status" (fn [] (inspector-status))))
       (when (and enabled? (not (:banner? @inspector-state-atom)))
         (swap! inspector-state-atom assoc :banner? true)
         (js/console.info
           (str "[yc-tracking] inspector ON (environment: " environment ")\n"
             "  console.table(ycTracking.events())  every dataLayer push the SPA attempted\n"
             "  ycTracking.last() / .clear() / .status()\n"
             "  ycTracking.disable() / .enable()  persists in localStorage "
             inspector-storage-key))))))

;; ---------------------------------------------------------------------------
;; Pushes

(defn push-to-datalayer!
  "Push an event to the Google Tag Manager dataLayer.
   Checks for existence of dataLayer before pushing."
  [event-data]
  #?(:cljs
     (let [pushed? (and (exists? js/window) (exists? js/dataLayer))]
       (tap! event-data pushed? false)
       (when pushed?
         (.push js/dataLayer (clj->js event-data))))))

(defn push-ecommerce-event!
  "Push an event that carries an :ecommerce object (or a Meta `eventID`),
   clearing the previous one first. GTM Data Layer Variables merge rather
   than replace across pushes, so without the clearing push product B
   inherits product A's ecommerce data within the same document, and a Meta
   tag could reuse an earlier event's `eventID`."
  [event-data]
  #?(:cljs
     (let [pushed? (and (exists? js/window) (exists? js/dataLayer))]
       (tap! event-data pushed? true)
       (when pushed?
         (.push js/dataLayer (clj->js {:event "yc.ecommerce_clear" :ecommerce nil :eventID nil}))
         (.push js/dataLayer (clj->js event-data))))))

(defn- ->gtm-keys
  "Rename transport-safe keys to the exact GTM dataLayer variable names —
   \"Purchase Type\" contains a space, which is illegal in an EDN keyword."
  [event]
  (if-let [purchase-type (:purchase_type event)]
    (-> event
      (dissoc :purchase_type)
      (assoc "Purchase Type" purchase-type))
    event))

(defn process-ga-events!
  "Push a list of GA tracking events from the backend to the dataLayer
   sequentially, clearing the ecommerce object ahead of events that carry
   one."
  [events]
  #?(:cljs
     (when (seq events)
       (doseq [event events]
         (let [event (->gtm-keys event)]
           (if (:ecommerce event)
             (push-ecommerce-event! event)
             (push-to-datalayer! event)))))))

(defn- storage-guard!
  "True exactly once per key for the given web storage — subsequent calls
   (and calls from other fire paths racing on the same key) return false.
   Safe no-op false when storage is blocked, so callers must pair it with
   an in-memory fallback if blocked browsers matter."
  [storage k]
  #?(:cljs
     (try
       (if (.getItem storage k)
         false
         (do (.setItem storage k "1") true))
       (catch :default _ false))
     :clj false))

(defn fire-purchase-once!
  "Push the purchase events for an order exactly once per browser —
   the confirmation page can be refreshed or revisited, and the GTM
   conversion tags must not double-count. Guarded via localStorage.
   The guard is only consumed when dataLayer exists — otherwise a page
   where GTM failed to load would silently burn the one shot."
  [order-id events]
  #?(:cljs
     (when (and order-id (seq events)
             (exists? js/window) (exists? js/dataLayer))
       (when (storage-guard! js/localStorage (str "yc-ga-purchase-" order-id))
         (process-ga-events! events)
         ;; The email-series funnel event has no GTM tag yet (BI wires it up
         ;; per the tracking restoration plan); pushing it is inert until then
         (push-to-datalayer! {:event "email_series_purchase_funnel"})))))

(defn fire-order-event-once!
  "Push a bare custom event exactly once per order per browser
   (localStorage guard — an order is globally unique and immutable)."
  [order-id event-name]
  #?(:cljs
     (when (and order-id event-name
             (exists? js/window) (exists? js/dataLayer))
       (when (storage-guard! js/localStorage
               (str "yc-ga-" event-name "-" order-id))
         (push-to-datalayer! {:event event-name})))))

(defn fire-checkout-step-once!
  "Push checkout-step events exactly once per cart per tab. sessionStorage
   (not localStorage) — a checkout visit is per-tab/per-session, survives
   refresh and gateway returns, and a returning customer's next cart gets a
   fresh uuid. `events` is a seq of event maps pushed in order."
  [cart-uuid step-key events]
  #?(:cljs
     (when (and cart-uuid step-key (seq events)
             (exists? js/window) (exists? js/dataLayer))
       (when (storage-guard! js/sessionStorage
               (str "yc-ck-" cart-uuid "-" (name step-key)))
         (process-ga-events! events)))))
